import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { useLocale } from '../../hooks/useLocale';

interface TranslateErrorModalProps {
  open: boolean;
  errorMessage: string;
  onClose: () => void;
}

const DANGER_GRADIENT = 'linear-gradient(135deg, #FF6B8A 0%, #FF1744 100%)';

const TranslateErrorModal: React.FC<TranslateErrorModalProps> = ({
  open,
  errorMessage,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useLocale();
  const isDark = theme.palette.mode === 'dark';

  const surface = isDark ? '#1A1D2E' : '#FFFFFF';
  const textPrimary = isDark ? '#F5F6FA' : '#13151F';
  const textSecondary = isDark ? '#A4AAC6' : '#5A6178';
  const borderColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(19,21,31,0.08)';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          margin: { xs: '16px', sm: '32px' },
          width: { xs: 'calc(100% - 32px)', sm: 420 },
          maxWidth: { xs: 'calc(100% - 32px)', sm: 420 },
          borderRadius: '24px',
          background: surface,
          color: textPrimary,
          border: `1px solid ${borderColor}`,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: isDark
            ? '0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,23,68,0.14)'
            : '0 24px 60px rgba(19,21,31,0.18), 0 0 0 1px rgba(255,23,68,0.08)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? 'rgba(5,6,15,0.72)' : 'rgba(19,21,31,0.55)',
          backdropFilter: 'blur(8px)',
        },
      }}
    >
      <Box aria-hidden sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: DANGER_GRADIENT,
      }} />

      <IconButton
        onClick={onClose}
        aria-label="close"
        size="small"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: textSecondary,
          '&:hover': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(19,21,31,0.05)',
            color: textPrimary,
          },
        }}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>

      <Box sx={{
        px: { xs: 3, sm: 4 },
        pt: { xs: 4, sm: 5 },
        pb: { xs: 3, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 2, sm: 2.5 },
      }}>
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isDark ? 'rgba(255,23,68,0.12)' : 'rgba(255,23,68,0.08)',
          color: '#FF1744',
        }}>
          <ErrorOutlineRoundedIcon sx={{ fontSize: 30 }} />
        </Box>

        <Stack spacing={1} alignItems="center">
          <Typography
            component="h2"
            sx={{
              fontFamily: '"SF Pro Display", "Inter", sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.375rem' },
              textAlign: 'center',
              color: textPrimary,
              letterSpacing: '-0.01em',
            }}
          >
            {t('translate_error_title')}
          </Typography>
          <Typography sx={{
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
            color: textSecondary,
            textAlign: 'center',
            lineHeight: 1.6,
          }}>
            {t('translate_error_message', { error: errorMessage })}
          </Typography>
        </Stack>

        <Button
          onClick={onClose}
          fullWidth
          disableElevation
          sx={{
            mt: 0.5,
            height: 48,
            borderRadius: '14px',
            color: '#fff',
            background: DANGER_GRADIENT,
            fontWeight: 700,
            fontSize: '0.9375rem',
            textTransform: 'none',
            boxShadow: '0 8px 24px rgba(255,23,68,0.3), inset 0 1px 0 rgba(255,255,255,0.25)',
            '&:hover': {
              background: DANGER_GRADIENT,
              boxShadow: '0 12px 30px rgba(255,23,68,0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
            },
          }}
        >
          {t('translate_error_btn_ok')}
        </Button>
      </Box>
    </Dialog>
  );
};

export default TranslateErrorModal;
